<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { useStoreGetters, useStore } from 'dashboard/composables/store';

import AddSharedFile from './AddSharedFile.vue';
import EditSharedFile from './EditSharedFile.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedSharedFile = ref({});

const sharedFiles = computed(() => getters['sharedFiles/getSharedFiles'].value);
const uiFlags = computed(() => getters['sharedFiles/getUIFlags'].value);
const deleteMessage = computed(() => ` ${selectedSharedFile.value.name}?`);

const dateFormat = timestamp => {
  return format(new Date(timestamp * 1000), 'dd MMM, yyyy');
};

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedSharedFile.value = response;
};

const openNewTab = response => {
  const fileUrl = response.file_url;
  if (fileUrl) {
    window.open(fileUrl, '_blank');
  } else {
    useAlert(t('SHARED_FILE_MGMT.FORM.NO_FILE_URL'));
  }
};

const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedSharedFile.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteSharedFile = async id => {
  try {
    await store.dispatch('sharedFiles/delete', id);
    useAlert(t('SHARED_FILE_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('SHARED_FILE_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedSharedFile.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedSharedFile.value.id] = true;
  closeDeletePopup();
  deleteSharedFile(selectedSharedFile.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('SHARED_FILE_MGMT.LIST.TABLE_HEADER.NAME'),
    t('SHARED_FILE_MGMT.LIST.TABLE_HEADER.FILE_NAME'),
    t('SHARED_FILE_MGMT.LIST.TABLE_HEADER.USED_TIMES'),
    t('SHARED_FILE_MGMT.LIST.TABLE_HEADER.ADD_BY'),
    t('SHARED_FILE_MGMT.LIST.TABLE_HEADER.CREATED_AT'),
  ];
});

onBeforeMount(() => {
  store.dispatch('sharedFiles/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('SHARED_FILE_MGMT.LOADING')"
    :no-records-found="!sharedFiles.length"
    :no-records-message="$t('SHARED_FILE_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('SHARED_FILE_MGMT.HEADER')"
        :description="$t('SHARED_FILE_MGMT.DESCRIPTION')"
        feature-name="sharedFiles"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('SHARED_FILE_MGMT.HEADER_BTN_TXT')"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="(sharedFile, index) in sharedFiles" :key="sharedFile.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="mb-1 font-medium break-words text-n-slate-12">
                {{ sharedFile.name }}
              </span>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 flex flex-row">
              <div>
                <img
                  v-if="sharedFile.file_type === 'image'"
                  :src="sharedFile.file_url"
                  alt="Shared File"
                  class="w-10 h-10 object-cover rounded mr-2"
                />
                <Icon
                  v-else-if="sharedFile.file_type === 'video'"
                  icon="i-ri-video-line"
                  class="text-n-slate-12 size-10 mr-2"
                />
                <Icon
                  v-else-if="sharedFile.file_type === 'document'"
                  icon="i-ri-file-word-line"
                  class="text-n-slate-12 size-10 mr-2"
                />
                <Icon
                  v-else
                  icon="i-ri-file-line"
                  class="text-n-slate-12 size-10 mr-2"
                />
              </div>
              <div>
                <p class="mb-0">{{ sharedFile.file_name }}</p>
                <span class="text-xs text-n-slate-11">
                  {{ `${sharedFile.file_size} KB` }}
                </span>
              </div>
            </td>
            <td class="py-4 leading-6 ltr:pr-4 rtl:pl-4">
              {{ sharedFile.messages_count }}
            </td>
            <td class="py-4 leading-6 ltr:pr-4 rtl:pl-4">
              <div class="flex flex-col justify-start">
                <p class="mb-0">{{ sharedFile.user.name }}</p>
                <p class="text-xs text-n-slate-11">
                  {{ sharedFile.user.email }}
                </p>
              </div>
            </td>
            <td class="py-4 leading-6 ltr:pr-4 rtl:pl-4">
              {{ dateFormat(sharedFile.created_at) }}
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('SHARED_FILE_MGMT.FORM.VIEW')"
                  icon="i-lucide-eye"
                  slate
                  xs
                  faded
                  :is-loading="loading[sharedFile.id]"
                  @click="openNewTab(sharedFile)"
                />
                <Button
                  v-tooltip.top="$t('SHARED_FILE_MGMT.FORM.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  :is-loading="loading[sharedFile.id]"
                  @click="openEditPopup(sharedFile)"
                />
                <Button
                  v-tooltip.top="$t('SHARED_FILE_MGMT.FORM.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  :is-loading="loading[sharedFile.id]"
                  @click="openDeletePopup(sharedFile, index)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddSharedFile @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditSharedFile
        :selected-response="selectedSharedFile"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('SHARED_FILE_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('SHARED_FILE_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('SHARED_FILE_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('SHARED_FILE_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
